import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAmazonAccounts, deleteAmazonAccount, initiateAmazonAuth, saveListing } from './api';
import type { MarketplaceCode } from './types';

const AMAZON_ACCOUNTS_QUERY_KEY = ['amazon', 'accounts'];

/**
 * Amazon Seller Accounts一覧を取得するフック
 *
 * React Queryのキャッシュ機能により、複数のページで使用しても
 * 最初のリクエスト以降はキャッシュから取得されるため、
 * パフォーマンスへの影響は最小限です。
 */
export function useAmazonAccounts() {
  const query = useQuery({
    queryKey: AMAZON_ACCOUNTS_QUERY_KEY,
    queryFn: getAmazonAccounts,
    staleTime: 5 * 60 * 1000, // 5分間はキャッシュを新鮮とみなす
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    // data を accounts としても提供（後方互換性のため）
    accounts: query.data || [],
  };
}

/**
 * Amazon Seller Accountを削除するフック
 */
export function useDeleteAmazonAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAmazonAccount,
    onSuccess: () => {
      // 削除成功後、アカウント一覧を再取得
      queryClient.invalidateQueries({ queryKey: AMAZON_ACCOUNTS_QUERY_KEY });
    },
  });
}

/**
 * Amazon認証を開始するフック
 */
export function useInitiateAmazonAuth() {
  return useMutation({
    mutationFn: (marketplace: MarketplaceCode = 'JP') => initiateAmazonAuth(marketplace),
  });
}

/**
 * Listings Items APIから商品を保存するフック
 */
export function useSaveListing() {
  return useMutation({
    mutationFn: ({ sku, asin, accountId }: { sku: string; asin: string; accountId: string }) =>
      saveListing(sku, asin, accountId),
  });
}
